import axiosInstance from '../axios/axiosInstance';
import type { PaginatedResponse } from './types';

export interface SuperAdmin {
  _id: string;
  email: string;
  name?: string;
  status: 'active' | 'pending' | 'deactivated';
  twoFactorEnabled: boolean;
  createdAt: string;
}

export interface SuperAdminFilters {
  page?: number;
  limit?: number;
  search?: string;
}

export interface InviteSuperAdminPayload {
  email: string;
}

export interface UpdateProfilePayload {
  name?: string;
}

export interface ChangePasswordPayload {
  currentPassword: string;
  newPassword: string;
}

export const superAdminsApi = {
  /**
   * Get all super admins with filters and pagination
   */
  getAll: async (params: SuperAdminFilters): Promise<PaginatedResponse<SuperAdmin>> => {
    const { data } = await axiosInstance.get<PaginatedResponse<SuperAdmin>>('/super-admins', {
      params,
    });
    return data;
  },

  /**
   * Invite a new super admin
   */
  invite: async (payload: InviteSuperAdminPayload): Promise<void> => {
    await axiosInstance.post('/super-admins/invite', payload);
  },

  /**
   * Resend invitation to pending super admin
   */
  resendInvite: async (email: string): Promise<void> => {
    await axiosInstance.post('/super-admins/resend-invite', { email });
  },

  /**
   * Update current super admin profile
   */
  updateProfile: async (payload: UpdateProfilePayload): Promise<SuperAdmin> => {
    const { data } = await axiosInstance.patch<SuperAdmin>(
      '/super-admins/account/profile',
      payload,
    );
    return data;
  },

  /**
   * Change current super admin password
   */
  changePassword: async (payload: ChangePasswordPayload): Promise<void> => {
    await axiosInstance.post('/super-admins/account/change-password', payload);
  },

  // ==================== Manage Other Super Admins ====================

  /**
   * Update another super admin's profile
   */
  updateAdmin: async (id: string, payload: UpdateProfilePayload): Promise<SuperAdmin> => {
    const { data } = await axiosInstance.patch<SuperAdmin>(`/super-admins/${id}`, payload);
    return data;
  },

  /**
   * Deactivate another super admin
   */
  deactivateAdmin: async (id: string): Promise<void> => {
    await axiosInstance.delete(`/super-admins/${id}`);
  },

  /**
   * Reset 2FA for another super admin
   */
  reset2FA: async (id: string): Promise<void> => {
    await axiosInstance.post(`/super-admins/${id}/reset-2fa`);
  },
};
